import { useState, useEffect } from 'react';
import api from '@/api';
import { useAuth } from '@/context/AuthContext';
import { Plus, Loader2, FileText, Search, Download, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import FilterBar, { FilterSelect, FilterInput, FilterDate } from '@/components/FilterBar';
import useRepairCategories from '@/hooks/useRepairCategories';

const PAYMENT_COLORS = {
  paid:'text-[var(--success)] bg-[var(--success-muted)] border-[var(--success)]',
  unpaid:'text-[var(--warning)] bg-[var(--warning-muted)] border-[var(--warning)]',
  overdue:'text-[var(--error)] bg-[var(--error-muted)] border-[var(--error)]',
  estimate_received:'text-[#8B5CF6] bg-[#8B5CF6]/10 border-[#8B5CF6]',
  pending_approval:'text-[#F59E0B] bg-[#F59E0B]/10 border-[#F59E0B]',
};
const STATUS_LABELS = {
  paid: 'Paid', unpaid: 'Unpaid', overdue: 'Overdue',
  estimate_received: 'Estimate Received', pending_approval: 'Pending Approval',
};
const EMPTY = { customer_id:'', payment_status:'', category:'', vendor:'', unit_search:'', invoice_search:'', date_from:'', date_to:'', amount_min:'', amount_max:'' };

export default function Invoices() {
  const { user } = useAuth();
  const { names: CATS } = useRepairCategories();
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY);
  const [customers, setCustomers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [createCustomers, setCreateCustomers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [form, setForm] = useState({ customer_id:'', truck_id:'', invoice_number:'', amount:'', payment_status:'unpaid', category:'Other', vendor_name:'', notes:'' });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const activeCount = Object.values(filters).filter(v => v !== '').length;

  useEffect(() => {
    api.get('/customers', { params: { limit: 200 } }).then(r => setCustomers(r.data.items || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [filters]);

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''));
      params.limit = 50;
      const res = await api.get('/invoices', { params });
      setInvoices(res.data.items);
      setTotal(res.data.total);
    } catch (e) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const setF = (key, val) => setFilters(p => ({ ...p, [key]: val }));
  const clearFilters = () => setFilters(EMPTY);

  const openCreate = async () => {
    const r = await api.get('/customers', { params: { limit: 100 } });
    setCreateCustomers(r.data.items || []);
    setShowCreate(true);
  };

  const loadTrucks = async (cid) => {
    if (!cid) return setTrucks([]);
    const r = await api.get('/trucks', { params: { customer_id: cid } });
    setTrucks(r.data);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/invoices', { ...form, amount: Number(form.amount) });
      toast.success('Invoice created!');
      setShowCreate(false);
      setForm({ customer_id:'', truck_id:'', invoice_number:'', amount:'', payment_status:'unpaid', category:'Other', vendor_name:'', notes:'' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const updatePayment = async (id, status) => {
    try {
      await api.put(`/invoices/${id}`, { payment_status: status });
      toast.success(`Marked as ${status}`);
      load();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/invoices/${deleteTarget.id}`);
      toast.success('Invoice deleted');
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const exportQuickBooks = async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''));
      const response = await api.get('/invoices/export/quickbooks', { 
        params,
        responseType: 'blob' 
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().slice(0,10);
      link.setAttribute('download', `quickbooks_invoices_${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('QuickBooks CSV exported!');
    } catch (e) {
      toast.error('Export failed');
    }
  };

  const fmt = (n) => new Intl.NumberFormat('en-US', { style:'currency', currency:'USD' }).format(n || 0);
  const totals = { total: invoices.reduce((a,i) => a+i.amount, 0), unpaid: invoices.filter(i => i.payment_status!=='paid').reduce((a,i) => a+i.amount, 0) };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="invoices-page">
      <div className="flex items-center justify-between pb-3 border-b" style={{ borderColor: 'var(--border)' }}>
        <div>
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] mb-1" style={{ color: 'var(--text-faint)' }}>// FINANCE</p>
          <h1 className="font-['Manrope'] font-extrabold text-2xl" style={{ color: 'var(--text-primary)' }}>
            Invoices
            {!loading && <span className="ml-3 text-sm font-['JetBrains_Mono'] font-normal" style={{ color: 'var(--text-faint)' }}>{total} results</span>}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={exportQuickBooks} data-testid="export-quickbooks-btn"
            className="flex items-center gap-2 px-4 py-2 border rounded-md font-['Manrope'] font-bold text-sm uppercase tracking-wide transition-colors"
            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.color = 'var(--primary)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; }}>
            <Download size={14} /> QuickBooks CSV
          </button>
          <button onClick={openCreate} data-testid="create-invoice-btn"
            className="btn-primary flex items-center gap-2 text-sm uppercase tracking-wide">
            <Plus size={14} /> New Invoice
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label:'Total Shown', value:fmt(totals.total) },
          { label:'Outstanding', value:fmt(totals.unpaid) },
          { label:'Invoice Count', value:invoices.length },
        ].map(({label,value}) => (
          <div key={label} className="card-slate p-4">
            <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-2" style={{ color: 'var(--text-faint)' }}>{label}</p>
            <p className="text-xl font-bold font-['JetBrains_Mono']" style={{ color: 'var(--text-primary)' }}>{value}</p>
          </div>
        ))}
      </div>

      {/* Filter Panel */}
      <FilterBar onClear={clearFilters} activeCount={activeCount}>
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-faint)' }} />
          <input value={filters.invoice_search} onChange={e => setF('invoice_search', e.target.value)}
            placeholder="Search invoice #, repair ID..."
            className="w-full input-slate pl-8"
            data-testid="invoice-search-input" />
        </div>
        <div className="flex flex-wrap gap-3">
          <FilterSelect label="Payment" value={filters.payment_status} onChange={v => setF('payment_status', v)} testId="filter-payment"
            options={[{value:'estimate_received',label:'Estimate Received'},{value:'pending_approval',label:'Pending Approval'},{value:'unpaid',label:'Unpaid'},{value:'paid',label:'Paid'},{value:'overdue',label:'Overdue'}]} />
          <FilterSelect label="Category" value={filters.category} onChange={v => setF('category', v)}
            options={CATS.map(c => ({ value:c, label:c }))} />
          {user?.role !== 'customer' && (
            <FilterSelect label="Customer" value={filters.customer_id} onChange={v => setF('customer_id', v)}
              options={customers.map(c => ({ value:c.id, label:c.company_name }))} />
          )}
          <FilterInput label="Vendor" value={filters.vendor} onChange={v => setF('vendor', v)} placeholder="Shop name..." />
          <FilterInput label="Unit / VIN / Plate" value={filters.unit_search} onChange={v => setF('unit_search', v)} placeholder="ATL-001..." />
          <FilterDate label="From" value={filters.date_from} onChange={v => setF('date_from', v)} />
          <FilterDate label="To" value={filters.date_to} onChange={v => setF('date_to', v)} />
        </div>
        <div className="flex flex-wrap gap-3">
          <FilterInput label="Min Amount ($)" value={filters.amount_min} onChange={v => setF('amount_min', v)} placeholder="0" />
          <FilterInput label="Max Amount ($)" value={filters.amount_max} onChange={v => setF('amount_max', v)} placeholder="9999" />
        </div>
      </FilterBar>

      {/* Table */}
      <div className="card-slate overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--primary)' }} /></div>
        ) : (
          <table className="w-full table-comfortable" data-testid="invoices-table">
            <thead>
              <tr>
                {['INVOICE #','CLIENT','UNIT','CATEGORY','AMOUNT','STATUS','ACTIONS'].map(h => (
                  <th key={h} className={['CATEGORY'].includes(h)?'hidden lg:table-cell':''}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-16">
                  <FileText size={28} className="mx-auto mb-3" style={{ color: 'var(--border)' }} />
                  <p className="font-['IBM_Plex_Sans'] text-sm" style={{ color: 'var(--text-faint)' }}>No invoices match your filters</p>
                </td></tr>
              ) : invoices.map((inv) => (
                <tr key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                  <td className="font-['JetBrains_Mono'] text-xs" style={{ color: 'var(--primary)' }}>{inv.invoice_number}</td>
                  <td className="font-['IBM_Plex_Sans']" style={{ color: 'var(--text-primary)' }}>{inv.customer_name}</td>
                  <td className="font-['JetBrains_Mono'] text-xs" style={{ color: 'var(--text-muted)' }}>{inv.truck_unit_number||'—'}</td>
                  <td className="text-xs hidden lg:table-cell" style={{ color: 'var(--text-muted)' }}>{inv.category||'—'}</td>
                  <td className="font-['JetBrains_Mono'] font-bold" style={{ color: 'var(--text-primary)' }}>{fmt(inv.amount)}</td>
                  <td>
                    <select
                      value={inv.payment_status}
                      onChange={(e) => updatePayment(inv.id, e.target.value)}
                      data-testid={`invoice-status-${inv.id}`}
                      title="Change payment status"
                      className={`cursor-pointer px-2 py-1 rounded-full text-[9px] font-bold uppercase tracking-wide border font-['JetBrains_Mono'] outline-none focus:ring-2 focus:ring-[var(--primary)] ${PAYMENT_COLORS[inv.payment_status]||PAYMENT_COLORS.unpaid}`}
                      style={{ appearance: 'none', WebkitAppearance: 'none', paddingRight: '1.5rem', backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'8\' height=\'8\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'3\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.4rem center', backgroundSize: '0.6rem' }}>
                      {Object.entries(STATUS_LABELS).map(([value, label]) => (
                        <option key={value} value={value} style={{ background: 'var(--surface)', color: 'var(--text-primary)' }}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="whitespace-nowrap">
                    <div className="flex items-center gap-1.5 justify-end">
                      {inv.pdf_url && (
                        <a href={`${process.env.REACT_APP_BACKEND_URL}${inv.pdf_url}?token=${localStorage.getItem('mm_token')}`} target="_blank" rel="noopener noreferrer"
                          className="px-2 py-1 text-[9px] font-bold uppercase border rounded-md transition-colors font-['JetBrains_Mono']"
                          style={{ color: 'var(--primary)', borderColor: 'var(--primary)' }}>
                          PDF
                        </a>
                      )}
                      <button onClick={() => setDeleteTarget({ id: inv.id, name: inv.invoice_number })}
                        data-testid={`delete-invoice-${inv.id}`}
                        title="Delete invoice"
                        className="p-1.5 rounded transition-colors"
                        style={{ color: 'var(--text-faint)' }}
                        onMouseEnter={e => { e.currentTarget.style.color = 'var(--error)'; e.currentTarget.style.background = 'var(--error-muted)'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-faint)'; e.currentTarget.style.background = 'transparent'; }}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} className="max-w-lg">
          <DialogHeader><DialogTitle style={{ color: 'var(--text-primary)' }} className="font-['Manrope']">New Invoice</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>Customer *</label>
                <select value={form.customer_id} onChange={e => { setForm(p=>({...p,customer_id:e.target.value,truck_id:''})); loadTrucks(e.target.value); }} required
                  className="w-full select-slate">
                  <option value="">Select...</option>
                  {createCustomers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>Unit</label>
                <select value={form.truck_id} onChange={e => setForm(p=>({...p,truck_id:e.target.value}))}
                  className="w-full select-slate">
                  <option value="">Select...</option>
                  {trucks.map(t => <option key={t.id} value={t.id}>{t.unit_number}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>Invoice # *</label>
                <input type="text" required value={form.invoice_number} onChange={e => setForm(p=>({...p,invoice_number:e.target.value}))}
                  className="w-full input-slate" />
              </div>
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>Amount ($) *</label>
                <input type="number" required step="0.01" value={form.amount} onChange={e => setForm(p=>({...p,amount:e.target.value}))}
                  className="w-full input-slate" />
              </div>
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>Category</label>
                <select value={form.category} onChange={e => setForm(p=>({...p,category:e.target.value}))}
                  className="w-full select-slate">
                  {CATS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>Status</label>
                <select value={form.payment_status} onChange={e => setForm(p=>({...p,payment_status:e.target.value}))}
                  className="w-full select-slate">
                  {['estimate_received','pending_approval','unpaid','paid','overdue'].map(s => <option key={s} value={s}>{STATUS_LABELS[s]||s}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>Vendor</label>
                <input type="text" value={form.vendor_name} onChange={e => setForm(p=>({...p,vendor_name:e.target.value}))}
                  className="w-full input-slate" />
              </div>
            </div>
            <button type="submit" disabled={saving} data-testid="create-invoice-submit"
              className="w-full btn-primary flex items-center justify-center gap-2 text-sm uppercase tracking-wide">
              {saving && <Loader2 size={14} className="animate-spin" />} Create Invoice
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--error)' }} className="font-['Manrope']">Delete Invoice</DialogTitle>
          </DialogHeader>
          <p className="text-sm font-['IBM_Plex_Sans'] mt-2" style={{ color: 'var(--text-muted)' }}>
            Are you sure you want to delete invoice <span className="font-bold font-['JetBrains_Mono']" style={{ color: 'var(--text-primary)' }}>{deleteTarget?.name}</span>? This cannot be undone.
          </p>
          <DialogFooter className="mt-4 gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>Cancel</Button>
            <Button onClick={handleDelete} disabled={deleting} style={{ background: 'var(--error)', color: 'white' }} data-testid="confirm-delete-invoice">
              {deleting ? <Loader2 size={14} className="animate-spin mr-2" /> : <Trash2 size={14} className="mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
